import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import { Field, Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type { BulkTagResult, TagVocabularyResponse, VoterSegmentEntity } from '@/shared/api/types'
import {
  BULK_TAG_MAX_IDS, bulkApplyTag, bulkRemoveTag, listSegments, type BulkTagTarget,
} from './api'
import { TagPicker, useTagVocabulary } from './tags'

/**
 * Tag many voters at once.
 *
 * ## Two targets, never both
 *
 * The selection on the list, or a saved segment. The server refuses a request
 * carrying both — a client that sends both has a bug, and a precedence rule
 * would hide it — so this dialog makes the choice a radio rather than two
 * fields that can each be filled in.
 *
 * A segment is used by ID, not by re-evaluating its rule here. A second place
 * that evaluates a definition is a second place that can disagree with the
 * server about what a rule means.
 *
 * ## The result is four numbers, and all four are shown
 *
 * `requested = applied + alreadyPresent + outOfScope + notFound`. Showing only
 * "applied" would let a ward coordinator tag 900 people, reach 200 because the
 * rest were outside their scope, and believe the job was done. Out-of-scope ids
 * are filtered and counted rather than throwing, precisely so the operator gets
 * a number instead of a 403 on the whole batch.
 */
export function BulkTagDialog({
  mode, selectedIds, onClose, onDone,
}: {
  mode: 'apply' | 'remove'
  selectedIds: string[]
  onClose: () => void
  onDone: () => void
}) {
  const vocab = useTagVocabulary({ activeOnly: mode === 'apply' })
  const [target, setTarget] = useState<'selection' | 'segment'>(
    selectedIds.length > 0 ? 'selection' : 'segment')
  const [segments, setSegments] = useState<VoterSegmentEntity[] | null>(null)
  const [segmentId, setSegmentId] = useState('')
  const [tag, setTag] = useState<TagVocabularyResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<BulkTagResult | null>(null)

  useEffect(() => {
    let alive = true
    listSegments()
      .then((r) => { if (alive) setSegments(r) })
      .catch(() => { if (alive) setSegments([]) })
    return () => { alive = false }
  }, [])

  const segment = segments?.find((s) => s.id === segmentId)
  const overCap = target === 'selection' && selectedIds.length > BULK_TAG_MAX_IDS
  // The server 400s on a segment that has never been refreshed — its member table
  // is empty, so "tag this segment" would silently tag nobody.
  const staleSegment = target === 'segment' && segment != null && !segment.lastRefreshedAt

  const ready = tag != null && !overCap && !staleSegment
    && (target === 'selection' ? selectedIds.length > 0 : Boolean(segmentId))

  const submit = async () => {
    if (!tag) return
    setBusy(true); setError(null)
    const body: BulkTagTarget = target === 'selection'
      ? { voterIds: selectedIds }
      : { segmentId }
    try {
      const fn = mode === 'apply' ? bulkApplyTag : bulkRemoveTag
      setResult(await fn(tag.tag, body))
      onDone()
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  const verb = mode === 'apply' ? 'Tag' : 'Untag'

  return (
    <Modal
      open
      onClose={onClose}
      width="lg"
      title={result ? `${verb}ged` : `${verb} many voters`}
      description={result ? undefined
        : mode === 'apply'
          ? 'Applies one tag to everybody in the target. Re-running it is safe — anyone who already has the tag is counted, not duplicated.'
          : 'Removes one tag from everybody in the target. Anyone who did not carry it is counted, not an error.'}
      footer={result ? (
        <button type="button" className="erp-btn-primary" onClick={onClose}>Close</button>
      ) : (
        <>
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="erp-btn-primary" disabled={!ready || busy}
            onClick={() => void submit()}>
            {busy ? `${verb}ging…` : verb}
          </button>
        </>
      )}
    >
      {result ? <BulkOutcome result={result} mode={mode} /> : (
        <div className="space-y-4">
          <ErrorStrip message={error ?? vocab.error} />

          <fieldset>
            <legend className="text-erp-xs font-medium text-muted-foreground mb-1.5">Who</legend>
            <div className="space-y-1.5">
              <label className="flex items-start gap-2 text-erp-sm">
                {/* `name` is what makes these ONE group. Two nameless radios are two
                    groups of one: arrow keys do not move between them, each is its own
                    tab stop, and a screen reader announces both as "1 of 1". */}
                <input type="radio" name="bulk-tag-target" className="mt-0.5"
                  checked={target === 'selection'}
                  disabled={selectedIds.length === 0}
                  onChange={() => setTarget('selection')} />
                <span>
                  The {fmtNumber(selectedIds.length)} selected {selectedIds.length === 1 ? 'voter' : 'voters'}
                  {selectedIds.length === 0 && (
                    <span className="block text-erp-xs text-muted-foreground">
                      Nothing is selected — tick rows on the list first.
                    </span>
                  )}
                </span>
              </label>
              <label className="flex items-start gap-2 text-erp-sm">
                <input type="radio" name="bulk-tag-target" className="mt-0.5"
                  checked={target === 'segment'}
                  onChange={() => setTarget('segment')} />
                <span>A saved segment</span>
              </label>
            </div>
          </fieldset>

          {overCap && (
            <Note tone="bad">
              {fmtNumber(selectedIds.length)} voters is over the {fmtNumber(BULK_TAG_MAX_IDS)} limit
              for one request. Save the selection as a segment, or work in pages.
            </Note>
          )}

          {target === 'segment' && (
            <Field label="Segment" required>
              <select className="erp-input w-full" value={segmentId}
                onChange={(e) => setSegmentId(e.target.value)}>
                <option value="">Choose a segment…</option>
                {(segments ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} · {fmtNumber(s.memberCount)} members
                    {s.lastRefreshedAt ? '' : ' (never refreshed)'}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {segment && (
            staleSegment ? (
              <Note tone="bad">
                “{segment.name}” has never been refreshed, so its membership table is empty and
                tagging it would tag nobody. Refresh it first — the server refuses this for the
                same reason.
              </Note>
            ) : (
              <p className="text-erp-xs text-muted-foreground">
                {fmtNumber(segment.memberCount)} members as of {fmtDateTime(segment.lastRefreshedAt)}.
                Membership is a snapshot: anyone who has since started matching the rule is not in it.
              </p>
            )
          )}

          <div>
            <div className="text-erp-xs font-medium text-muted-foreground mb-1.5">Which tag</div>
            {vocab.tags === null && (
              <p className="text-erp-xs text-muted-foreground">
                {vocab.error
                  ? 'The tag vocabulary could not be loaded, so there is nothing to pick from.'
                  : 'Loading the tag vocabulary…'}
              </p>
            )}
            {vocab.tags && (
              tag ? (
                <div className="flex items-center gap-2">
                  <span className="text-erp-sm">{tag.displayName}</span>
                  <span className="font-mono text-erp-xs text-muted-foreground">{tag.tag}</span>
                  <button type="button" className="erp-btn-ghost text-erp-xs ml-auto"
                    onClick={() => setTag(null)}>Change</button>
                </div>
              ) : (
                <TagPicker
                  vocabulary={vocab.tags}
                  onPick={setTag}
                  // `Modal` does not move focus into itself, so without this the focus
                  // stays on the button behind the overlay and Tab walks the page
                  // underneath the dialog.
                  autoFocus
                  placeholder={mode === 'apply' ? 'Search tags' : 'Search the tag to remove'}
                />
              )
            )}
            {mode === 'apply' && (
              <p className="mt-2 text-erp-xs text-muted-foreground">
                Bulk applying does not invent a tag. Create one on the tag manager first if the
                one you need is not here.
              </p>
            )}
          </div>

          <Note tone="info">
            This writes one audit entry for the whole batch, with the counts and the tag — never
            a row per voter, and never the voter ids. Who tagged each person, and when, is
            recorded against the voter.
          </Note>
        </div>
      )}
    </Modal>
  )
}

/**
 * The four-way split.
 *
 * The identity is asserted on screen, not just in a backend test: if the parts
 * ever stop summing to the total, the reader sees it here rather than trusting a
 * headline number that quietly lost some rows.
 */
function BulkOutcome({ result, mode }: { result: BulkTagResult; mode: 'apply' | 'remove' }) {
  const sum = result.applied + result.alreadyPresent + result.outOfScope + result.notFound
  const balances = sum === result.requested
  const changed = result.applied

  const parts = [
    {
      label: mode === 'apply' ? 'Tagged' : 'Untagged',
      value: result.applied,
      tone: 'good' as const,
      hint: mode === 'apply' ? 'Rows written.' : 'Rows deleted.',
    },
    {
      label: mode === 'apply' ? 'Already had it' : 'Did not have it',
      value: result.alreadyPresent,
      tone: 'neutral' as const,
      hint: 'Not an error — this operation is safe to re-run.',
    },
    {
      label: 'Outside your scope',
      value: result.outOfScope,
      tone: 'warn' as const,
      hint: 'Counted rather than refused, so one stray id does not fail the whole batch.',
    },
    {
      label: 'No such voter',
      value: result.notFound,
      tone: 'warn' as const,
      hint: 'The id does not exist — usually a merged or erased record.',
    },
  ]

  return (
    <div className="space-y-3">
      <div className={cn(
        'flex items-start gap-2 rounded-md border p-3 text-erp-sm',
        changed > 0
          ? 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200'
          : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200',
      )}>
        {changed > 0
          ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
          : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />}
        <div>
          <div className="font-medium">
            {fmtNumber(changed)} of {fmtNumber(result.requested)} voters{' '}
            {mode === 'apply' ? 'now carry' : 'no longer carry'}{' '}
            <span className="font-mono">{result.tag}</span>
          </div>
          {changed === 0 && (
            <div className="text-erp-xs mt-0.5">
              Nothing changed. The breakdown below says why.
            </div>
          )}
        </div>
      </div>

      <ul className="divide-y divide-border/60 rounded-md border border-border">
        {parts.map((p) => (
          <li key={p.label} className="flex items-baseline gap-3 px-3 py-2">
            <span className={cn(
              'h-1.5 w-1.5 rounded-full shrink-0',
              p.tone === 'good' && 'bg-emerald-600',
              p.tone === 'warn' && 'bg-amber-500',
              p.tone === 'neutral' && 'bg-muted-foreground/40',
            )} aria-hidden />
            <span className="text-erp-sm">{p.label}</span>
            <span className="text-erp-xs text-muted-foreground min-w-0 truncate">{p.hint}</span>
            <span className="ml-auto tabular-nums font-medium">{fmtNumber(p.value)}</span>
          </li>
        ))}
      </ul>

      <p className={cn('text-erp-xs', balances ? 'text-muted-foreground' : 'text-red-700 dark:text-red-300')}>
        {balances
          ? `The four add up to the ${fmtNumber(result.requested)} requested.`
          : `These add up to ${fmtNumber(sum)}, not the ${fmtNumber(result.requested)} requested — some voters are unaccounted for. Report this.`}
      </p>
    </div>
  )
}
